import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Subject, Observable, BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { takeUntil, map, switchMap, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { AuditService } from '../services/audit.service';
import { AuditLogEvent, EntityType } from '../models/audit-log';

@Component({
  selector: 'app-audit-list',
  templateUrl: './audit-list.component.html',
  styleUrls: ['./audit-list.component.scss']
})
export class AuditListComponent implements OnInit, OnDestroy {
  @Input() entityType!: EntityType;
  @Input() entityId!: string;

  private readonly destroying$ = new Subject<void>();
  private search$: BehaviorSubject<[string]> = new BehaviorSubject<[string]>(['']);
  auditEvents$!: Observable<AuditLogEvent[]>;

  expandedEventId: number | null = null;
  sortOrder: 'newest' | 'oldest' = 'newest';
  searchControl: FormControl = new FormControl<string | null>(null);
  public form: FormGroup = this.formBuilder.group({
    searchControl: this.searchControl
  });
  subscriptions: Subscription[] = [];

  constructor(
    private auditService: AuditService,
    private formBuilder: FormBuilder
  ) {}

  ngOnInit(): void {
    this.auditEvents$ = combineLatest([
      this.search$,
    ]).pipe(
      switchMap(([[searchString]]) =>
        this.auditService.getAuditLog(this.entityType, this.entityId).pipe(
          map(response => {
            const filteredEvents = this.filterAuditEvents(response, searchString);
            return this.sortAuditEvents(filteredEvents);
          }),
          takeUntil(this.destroying$)
        )
      )
    );
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  toggleEvent(eventId: number): void {
    this.expandedEventId = this.expandedEventId === eventId ? null : eventId;
  }

  isExpanded(eventId: number): boolean {
    return this.expandedEventId === eventId;
  }

  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'newest' ? 'oldest' : 'newest';
    this.refreshList();
  }

  protected refreshList(): void {
    this.search$.next([this.searchControl.value]);
  }

  private subscribeToChanges(): void {
    this.subscriptions.push(
      this.searchControl.valueChanges
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
        )
        .subscribe((_) => {
          this.refreshList();
        })
    );
  }

  private filterAuditEvents(events: AuditLogEvent[], searchString: string): AuditLogEvent[] {
    if (!searchString || searchString.trim() === '') {
      return events;
    }
    const term = searchString.toLowerCase().trim();
    return events.filter(event =>
      event.author?.toLowerCase().includes(term) ||
      event.eventType?.toLowerCase().includes(term) ||
      event.timestamp?.toLowerCase().includes(term) ||
      JSON.stringify(event.eventDetails)?.toLowerCase().includes(term)
    );
  }

  private sortAuditEvents(events: AuditLogEvent[]): AuditLogEvent[] {
    return events.sort((a, b) => {
      // First sort by timestamp
      const timestampA = new Date(a.timestamp).getTime();
      const timestampB = new Date(b.timestamp).getTime();
      
      if (timestampA !== timestampB) {
        return this.sortOrder === 'newest' ? timestampB - timestampA : timestampA - timestampB;
      }
      
      // If timestamps are equal, sort by ID as tiebreaker
      return this.sortOrder === 'newest' ? b.id - a.id : a.id - b.id;
    });
  }
} 